import { Link } from "react-router-dom";
import { Clock, Brain, HeartPulse, Users, ClipboardList, Compass, ArrowRight } from "lucide-react";
import Marquee from "@/components/Marquee";
import { Reveal, MaskedLines } from "@/components/Reveal";

const features = [
  { icon: Clock, title: "24/7 Clinical Oversight", text: "Awake, trained staff around the clock — safety and support never sleep." },
  { icon: Brain, title: "Cognitive Adaptation", text: "Care paced and adapted for residents with cognitive or functional impairments." },
  { icon: HeartPulse, title: "High-Intensity Structure", text: "Daily therapeutic programming, recovery education, and skill-building." },
  { icon: Users, title: "Peer Community", text: "Recovery happens together — shared meals, groups, and accountability." },
  { icon: ClipboardList, title: "Individualized Plans", text: "Every resident arrives with a story; every plan is built around it." },
  { icon: Compass, title: "Step-Down Pathways", text: "Coordinated transitions to lower levels of care, work, and independent living." },
];

const day = [
  { time: "7:00 AM", label: "Wake-up, breakfast & morning check-in" },
  { time: "9:00 AM", label: "Group therapy & recovery education" },
  { time: "12:00 PM", label: "Lunch & community time" },
  { time: "1:00 PM", label: "Individual sessions & life-skills work" },
  { time: "4:00 PM", label: "Wellness, recreation & peer support" },
  { time: "6:00 PM", label: "Dinner together as a house" },
  { time: "8:00 PM", label: "Evening reflection & 12-step or support meeting" },
  { time: "10:00 PM", label: "Quiet hours — rest for tomorrow" },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative overflow-hidden bg-[#FAF8F5] grain">
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#F3E8FF] blur-3xl opacity-80" />
        <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-16 pb-20 lg:pt-24 text-center relative">
          <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-4">ASAM 3.3 Services</p>
          <MaskedLines
            className="font-display font-black text-[#2E1065] text-4xl sm:text-5xl lg:text-6xl leading-[1.08] tracking-tight"
            lines={["Clinically managed.", "Deeply human."]}
          />
          <Reveal delay={0.4}>
            <p className="mt-7 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mx-auto" data-testid="services-intro">
              ASAM Level 3.3 is a clinically managed, high-intensity residential level of care for people whose
              recovery needs structure, time, and a slower, more supported pace — delivered here inside a real
              home, not an institution.
            </p>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-20 lg:py-28" data-testid="services-features">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <Reveal key={f.title} delay={i * 0.07}>
              <div className="bg-white rounded-3xl p-7 h-full border border-purple-100 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-500" data-testid={`service-card-${i}`}>
                <div className="w-12 h-12 rounded-2xl bg-[#F3E8FF] flex items-center justify-center mb-5">
                  <f.icon size={22} className="text-[#5B21B6]" />
                </div>
                <h3 className="font-display font-bold text-xl text-[#2E1065]">{f.title}</h3>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{f.text}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-[#F4EFE6] py-20 lg:py-28" data-testid="daily-schedule">
        <div className="max-w-4xl mx-auto px-5 sm:px-8">
          <Reveal className="text-center">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#B45309] mb-3">A Day of Hope</p>
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#1E1B4B]" data-testid="schedule-heading">
              The rhythm residents rebuild on
            </h2>
          </Reveal>
          <div className="mt-14 relative">
            <div className="absolute left-[19px] sm:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-[#5B21B6] via-[#F59E0B] to-[#5B21B6]" />
            {day.map((d, i) => (
              <Reveal key={d.time} delay={i * 0.05}>
                <div className={`relative flex items-start gap-5 mb-8 sm:w-1/2 ${i % 2 ? "sm:ml-auto sm:pl-10" : "sm:pr-10 sm:text-right sm:flex-row-reverse"} pl-12 sm:pl-0`} data-testid={`schedule-item-${i}`}>
                  <div className="absolute left-3 sm:left-auto sm:right-auto w-4 h-4 rounded-full bg-[#F59E0B] border-4 border-white shadow mt-1"
                    style={i % 2 ? { left: "auto", marginLeft: "-2.05rem" } : { left: "0.2rem" }}
                  />
                  <div className="bg-white rounded-2xl border border-purple-100 shadow-sm px-5 py-4 w-full">
                    <p className="text-xs font-bold text-[#B45309] uppercase tracking-wider">{d.time}</p>
                    <p className="text-sm font-semibold text-[#1E1B4B] mt-1">{d.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#2E1065] py-20 text-center relative grain" data-testid="services-cta">
        <Reveal>
          <h2 className="font-display font-black text-3xl sm:text-4xl text-[#FAF8F5]" data-testid="services-cta-heading">
            Ready to take the first step?
          </h2>
          <p className="mt-4 text-purple-200 max-w-xl mx-auto text-base">
            Referrals are open for the Petersburg Male Manor. We respond within one business day.{" "}
            <Link to="/admissions" data-testid="services-admissions-link" className="text-amber-400 font-bold hover:underline underline-offset-4">See who qualifies and how intake works</Link>.
          </p>
          <Link
            to="/contact"
            data-testid="services-cta-button"
            className="mt-8 inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-bold px-9 py-4 rounded-full shadow-xl shadow-amber-500/20 transition-colors duration-300"
          >
            Start a Referral <ArrowRight size={18} />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
